import { type ReactElement, useEffect, useRef } from 'react';
import type * as Adflow from 'adflow';

import { useTemplateWebMCPFeature } from './hooks/useTemplateWebMCPFeature';
import { rasterizePreviewElement } from './capturePreviewImage';
import { registerTemplatePreviewCapture } from './previewCaptureRegistry';

type RenderPreview = (args: Adflow.RenderPreviewArgs) => ReactElement;

type Props = {
  args: Adflow.RenderPreviewArgs;
  renderPreview: RenderPreview;
};

/**
 * Off-screen preview used for WebMCP raster capture (no selection overlays).
 * Registered only while cm-template-webmcp is enabled.
 */
export function WebMcpTemplatePreviewCapture({ args, renderPreview }: Props): ReactElement | null {
  const featureEnabled = useTemplateWebMCPFeature();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!featureEnabled) {
      return;
    }

    return registerTemplatePreviewCapture(async (options) => {
      const frame = containerRef.current?.querySelector('[data-testid="preview-frame"]');
      if (!(frame instanceof HTMLElement)) {
        throw new Error('The preview frame is not mounted.');
      }
      return rasterizePreviewElement(frame, options);
    });
  }, [featureEnabled]);

  if (!featureEnabled) {
    return null;
  }

  const captureArgs: Adflow.RenderPreviewArgs = {
    ...args,
    isIndexPreview: true,
    selectedElementId: null,
  };

  return (
    <div
      ref={containerRef}
      aria-hidden
      style={{
        position: 'fixed',
        left: -10000,
        top: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
      data-webmcp-preview-capture-root
    >
      {renderPreview(captureArgs)}
    </div>
  );
}
